import { z } from 'zod'
import { UTCMillisecondsSchema } from '../time'
import { CompanyIdSchema, idSchemaV1, isId, UserIdSchema, WorkflowIdSchema } from '../ids'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const ImportIdSchema = idSchemaV1('Import')
export type ImportId = z.infer<typeof ImportIdSchema>
export const isImportId = isId(ImportIdSchema)

export const WithImportIdSchema = z.object({
  importId: ImportIdSchema.optional().meta({
    id: 'importId',
    description: 'ID of the import that created this entity',
  }),
})
export type WithImportId = z.infer<typeof WithImportIdSchema>

export const IN_PROGRESS_IMPORT_STATUSES = ['Uploaded', 'Queued', 'Processing', 'PostProcessing'] as const
export const TERMINAL_IMPORT_STATUSES = ['Completed', 'Failed'] as const
export const IMPORT_STATUSES = ['New', ...IN_PROGRESS_IMPORT_STATUSES, ...TERMINAL_IMPORT_STATUSES] as const

export const ImportStatusSchema = z.enum(IMPORT_STATUSES)
export type ImportStatus = z.infer<typeof ImportStatusSchema>

export type InProgressImportStatus = (typeof IN_PROGRESS_IMPORT_STATUSES)[number]

const IN_PROGRESS_IMPORT_STATUS_SET: ReadonlySet<ImportStatus> = new Set(IN_PROGRESS_IMPORT_STATUSES)

export const isImportInProgress = (status: ImportStatus): status is InProgressImportStatus =>
  IN_PROGRESS_IMPORT_STATUS_SET.has(status)

export const ImportStatusHistoryEntrySchema = z.object({
  status: ImportStatusSchema,
  timestamp: UTCMillisecondsSchema,
})
export type ImportStatusHistoryEntry = z.infer<typeof ImportStatusHistoryEntrySchema>

export const IMPORT_ENTITIES = ['Account', 'Area', 'CalendarEvent', 'Contact', 'Task', 'User'] as const
export const ImportEntitySchema = z.enum(IMPORT_ENTITIES)
export type ImportEntity = z.infer<typeof ImportEntitySchema>

export const CREATABLE_IMPORT_ENTITIES = ['Account', 'Area', 'CalendarEvent', 'Task', 'User'] as const
export const CreatableImportEntitySchema = z.enum(CREATABLE_IMPORT_ENTITIES)
export type CreatableImportEntity = z.infer<typeof CreatableImportEntitySchema>

export const MAX_IMPORT_FILE_SIZE = 300 * 1024 ** 2
export const KML_IMPORT_CONTENT_TYPE = 'application/vnd.google-earth.kml+xml'
export const KMZ_IMPORT_CONTENT_TYPE = 'application/vnd.google-earth.kmz'

export const CSV_IMPORT_CONTENT_TYPE = 'text/csv'
export const AREA_IMPORT_CONTENT_TYPE = 'application/octet-stream'

export function getImportContentType(entity: ImportEntity): string {
  if (entity === 'Area') return AREA_IMPORT_CONTENT_TYPE
  return CSV_IMPORT_CONTENT_TYPE
}

export const ImportUploadContentTypeSchema = z.enum([
  CSV_IMPORT_CONTENT_TYPE,
  KML_IMPORT_CONTENT_TYPE,
  KMZ_IMPORT_CONTENT_TYPE,
])
export type ImportUploadContentType = z.infer<typeof ImportUploadContentTypeSchema>

export function getImportFileContentType(entity: ImportEntity, filename: string): ImportUploadContentType {
  if (entity !== 'Area') return CSV_IMPORT_CONTENT_TYPE
  if (filename.toLowerCase().endsWith('.kmz')) return KMZ_IMPORT_CONTENT_TYPE
  return KML_IMPORT_CONTENT_TYPE
}

export const MAX_RECORDS_WITHOUT_LAT_LNG = 5000

const ImportResultsSchema = z.object({
  successes: z.number(),
  errors: z.number(),
  skipped: z.number().default(0),
})
export type ImportResults = z.infer<typeof ImportResultsSchema>

/**
 * User-provided mapping from CSV column names to the parser's expected
 * header names. Keyed by the column as it appears in the uploaded file so
 * each CSV column can only be routed to one expected header.
 *
 * Keys are CSV column names (what the user uploaded).
 * Values are expected header names (what the parser needs).
 * Match is case-insensitive. CSV columns not in the mapping keep their
 * original name and the parser falls back to matching expected names
 * directly against it.
 */
export const ColumnMappingSchema = z.record(z.string(), z.string()).meta({
  id: 'ColumnMapping',
  description: 'Maps CSV column names (keys) to the expected header names the parser should read them as (values).',
})
export type ColumnMapping = z.infer<typeof ColumnMappingSchema>

export const UnsavedImportSchema = z.object({
  entity: ImportEntitySchema,
  name: z.string().optional(),
  disableGeocoding: z.boolean().optional().meta({
    description:
      'When true, skip the external geocoding lookup for an address that matches no existing location. When false or omitted, that lookup runs.',
  }),
  addMissingUsers: z.boolean().optional().meta({
    description: 'Create placeholder users for any owner or assignee not already in Terros.',
  }),
  createCustomLocations: z.boolean().optional().meta({
    description: 'After all enabled lookups miss, create a location using the address and coordinates from the CSV.',
  }),
  cleanupLocation: z.boolean().optional().meta({
    description:
      'Match imported addresses with our location data. When turned off, we use the location data in your file—unless its coordinates are within 10 meters of one of our locations. When omitted, imports keep the matching they used before this option existed.',
  }),
  notifyUsers: z.boolean().optional().meta({
    description: 'Send a welcome email with login instructions to each new user.',
  }),
  skipDuplicates: z.boolean().optional().meta({
    description:
      "When a row matches an account that already exists, skip it instead of updating it. Skipped accounts won't be modified or removed when this import is deleted.",
  }),
  assignAreasOnExisting: z.boolean().optional().meta({
    description:
      'When this import updates an account that already exists, also run areas that auto-assign on import. New accounts always get those areas. Defaults to true.',
  }),
  workflowId: WorkflowIdSchema.optional(),
  columnMapping: ColumnMappingSchema.optional(),
})
export type UnsavedImport = z.infer<typeof UnsavedImportSchema>

export const CreatableUnsavedImportSchema = z.object({
  ...UnsavedImportSchema.shape,
  entity: CreatableImportEntitySchema,
})
export type CreatableUnsavedImport = z.infer<typeof CreatableUnsavedImportSchema>

export const ImportDataSchema = z.object({
  ...UnsavedImportSchema.shape,
  ...ImportResultsSchema.shape,
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  importId: ImportIdSchema,
  ownerId: UserIdSchema,
  importStatus: ImportStatusSchema,
  statusHistory: ImportStatusHistoryEntrySchema.array().optional(),
  completedAt: UTCMillisecondsSchema.optional(),
  totalMessages: z.number(),
  companyId: CompanyIdSchema,
  headers: z.string().array(),
  reason: z.string().optional(),
  expiresAt: z.number().optional(),
  expectedRecords: z.number().optional().meta({
    description: 'Records this import saved, counted while post-processing waits for them to become searchable.',
  }),
  indexedRecords: z.number().optional().meta({
    description: 'Records this import saved that are searchable so far, counted during the same wait.',
  }),
})

export type ImportData = z.infer<typeof ImportDataSchema>

export const ImportObjectMetadataSchema = z.object({
  key: z.string(),
  signedUrl: z.string(),
})
export type ImportObjectMetadata = z.infer<typeof ImportObjectMetadataSchema>

export const ImportHeaderSchema = z.object({
  name: z.string(),
  description: z.string(),
  example: z.string().optional(),
  type: z.string().optional(),
  required: z.boolean().optional().meta({ description: 'defaults to false' }),
})
export type ImportHeader = z.infer<typeof ImportHeaderSchema>

/** Import workers name their process with this prefix, so audit logs show which records an import wrote. */
export const IMPORT_PROCESS_NAME_PREFIX = 'ImportScript'
